import { logout } from 'actions'
import axios from 'axios'
import { compile } from 'path-to-regexp'
import store from 'store'
import { TOKEN_EXP_LOCAL_STORAGE, TOKEN_KEY_LOCAL_STORAGE } from './constants'

const url = path => params => compile(path)(params)

export const urls = {
  ssoStart: url('/sso/start/'),
  login: url('/login/'),
  user: url('/user/'),
  bankrolls: url('/bankrolls/'),
  bankroll: url('/bankrolls/:id/'),
  authorization: url('/bankrolls/:id/authorization/'),
  players: url('/bankrolls/:id/players/'),
  sessions: url('/bankrolls/:bankrollId/sessions/'),
  session: url('/bankrolls/:bankrollId/sessions/:id/'),
  exportSessions: url('/bankrolls/:bankrollId/sessions/export/'),
  expenses: url('/bankrolls/:bankrollId/expenses/'),
  expense: url('/bankrolls/:bankrollId/expenses/:id/'),
  exportExpenses: url('/bankrolls/:bankrollId/expenses/export/'),
  customFields: url('/bankrolls/:bankrollId/fields/'),
  customField: url('/bankrolls/:bankrollId/fields/:id/'),
}

export const API_URL = process.env.REACT_APP_API_URL

const api = axios.create({
  baseURL: API_URL,
})

export default api

api.interceptors.request.use(
  config => {
    // Only include auth token when communicating with the API
    if (config.baseURL === API_URL && !config.headers.Authorization) {
      // remove an expired token
      const tokenExpiration = localStorage.getItem(TOKEN_EXP_LOCAL_STORAGE)
      if (!!tokenExpiration && new Date() >= new Date(tokenExpiration)) {
        localStorage.removeItem(TOKEN_KEY_LOCAL_STORAGE)
      }
      // set auth header with token
      const token = localStorage.getItem(TOKEN_KEY_LOCAL_STORAGE)
      if (token) {
        config.headers.Authorization = `Token ${token}`
      }
    }
    return config
  },
  error => Promise.reject(error),
)

api.interceptors.response.use(
  response => response,
  error => {
    if (!!error.response && error.response.status === 401) {
      store.dispatch(logout(false))
    }
    return Promise.reject(error)
  },
)

/**
 * Retreive all bankrolls
 */
export const fetchBankrolls = async () => {
  const resp = await api.get(urls.bankrolls(), {
    // If anyone has more than 100 bankrolls, then we can deal with this
    // when they complain. For now, we're not worrying about paginating.
    params: {
      per_page: 100,
    },
  })
  return resp.data
}

/**
 * Retrieve a bankroll
 */
export const fetchBankroll = async (id, params) => {
  const resp = await api.get(urls.bankroll({ id }), {
    params,
  })
  return resp.data
}

/**
 * Create a bankroll
 */
export const createBankroll = async bankroll => {
  const resp = await api.post(urls.bankrolls(), {
    ...bankroll,
  })
  return resp.data
}

/**
 * Update a bankroll
 */
export const updateBankroll = async (id, bankroll) => {
  const resp = await api.patch(urls.bankroll({ id }), {
    ...bankroll,
  })
  return resp.data
}

/**
 * Destroy a bankroll
 */
export const deleteBankroll = async id => {
  await api.delete(urls.bankroll({ id }))
}

/**
 * Retreive all players for a bankroll
 */
export const fetchPlayers = async id => {
  const resp = await api.get(urls.players({ id }))
  return resp.data
}

/**
 * Add a user
 */
export const addSharedUser = async (id, username) => {
  const resp = await api.post(urls.authorization({ id }), {
    username,
  })
  return resp.data
}

/**
 * Remove a user
 */
export const removeSharedUser = async (id, username) => {
  await api.delete(urls.authorization({ id }), {
    data: {
      username,
    },
  })
}

/**
 * Retrieve all sessions
 */
export const fetchSessions = async (bankrollId, params) => {
  const resp = await api.get(urls.sessions({ bankrollId }), {
    params,
  })
  return resp.data
}
/**
 * Retrieve a session
 */
export const fetchSession = async (bankrollId, id) => {
  const resp = await api.get(urls.session({ bankrollId, id }))
  return resp.data
}

/**
 * Create a session
 */
export const createSession = async (bankrollId, session) => {
  const resp = await api.post(urls.sessions({ bankrollId }), {
    ...session,
  })
  return resp.data
}

/**
 * Update a session
 */
export const updateSession = async (bankrollId, id, session) => {
  const resp = await api.put(urls.session({ bankrollId, id }), {
    ...session,
  })
  return resp.data
}

/**
 * Delete a session
 */
export const deleteSession = async (bankrollId, id) => {
  await api.delete(urls.session({ bankrollId, id }))
}

/**
 * Export session data
 */
export const exportSessions = async bankrollId => {
  const resp = await api.get(urls.exportSessions({ bankrollId }), {
    responseType: 'blob',
    timeout: 30000, // 30 seconds
  })
  return resp.data
}

/**
 * Retrieve all expenses
 */
export const fetchExpenses = async (bankrollId, params) => {
  const resp = await api.get(urls.expenses({ bankrollId }), {
    params,
  })
  return resp.data
}

/**
 * Retrieve an expense
 */
export const fetchExpense = async (bankrollId, id) => {
  const resp = await api.get(urls.expense({ bankrollId, id }))
  return resp.data
}

/**
 * Create an expense
 */
export const createExpense = async (bankrollId, expense) => {
  const resp = await api.post(urls.expenses({ bankrollId }), {
    ...expense,
  })
  return resp.data
}

/**
 * Update an expense
 */
export const updateExpense = async (bankrollId, id, expense) => {
  const resp = await api.put(urls.expense({ bankrollId, id }), {
    ...expense,
  })
  return resp.data
}

/**
 * Delete an expense
 */
export const deleteExpense = async (bankrollId, id) => {
  await api.delete(urls.expense({ bankrollId, id }))
}

/**
 * Export expense data
 */
export const exportExpenses = async bankrollId => {
  const resp = await api.get(urls.exportExpenses({ bankrollId }), {
    responseType: 'blob',
    timeout: 30000, // 30 seconds
  })
  return resp.data
}

/**
 * Create custom field
 */
export const createCustomField = async (bankrollId, field) => {
  const resp = await api.post(urls.customFields({ bankrollId }), {
    ...field,
  })
  return resp.data
}

/**
 * Update custom field
 */
export const updateCustomField = async (bankrollId, id, field) => {
  const resp = await api.patch(urls.customField({ bankrollId, id }), {
    ...field,
  })
  return resp.data
}

/**
 * Delete custom field
 */
export const deleteCustomField = async (bankrollId, id) => {
  await api.delete(urls.customField({ bankrollId, id }))
}
